import { EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { UserData } from '../auth-service/user-data.model';
import { HealthScoreResponseModel } from 'src/app/api/proxy/datapoints/datapoints-models';
import { AppAccessType, ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { LastFaceScan } from '../face-scan-service/LastFaceScan';
import { SessionConfigResponseModel, SummaryModel, WorkoutModel } from 'src/app/api/proxy/wearables/wearables-models';
import { ILocalAppEnvironment } from '../environment-config-service/environment-config.service';
import { HealthScoreHistory } from '../health-score-service/health-score-history';
import { ProfileScanHistoryModel } from 'src/app/api/proxy/facescan/facescan-models';
import { OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { NotificationItemWithArticle } from '../one-signal-service/one-signal.service';
import { EmotionsHistoryResponse } from 'src/app/api/proxy/emotion/emotion-models';
import { CurrentObservationStatusModel } from 'src/app/api/proxy/datapoints/models/current-observation-status-model';
import { MemberProfileDimensionModel } from 'src/app/api/proxy/datapoints/models/member-profile-dimension-model';
import { VivaBotFaceState } from 'src/app/controls/viva-chat/viva-chat.component';
import {
	MemberQuestionnaireHistoryModel,
	QuestionnaireRequestListItemModel,
} from 'src/app/api/proxy/questionnaire/questionnaire-models';
import { IInitialQuestion } from 'src/app/pages/home/media-chatbot/media-chatbot.page';

export interface RefreshProfileDataPayload {
	forceRefresh?: boolean;
	source?: string;
}

export class EventService {
	static readonly healthScoreUpdated: EventEmitter<HealthScoreResponseModel> = new EventEmitter<HealthScoreResponseModel>();
	static readonly faceScanUpdated: EventEmitter<LastFaceScan | null> = new EventEmitter<LastFaceScan | null>();
	static readonly myProfileUpdated: EventEmitter<ProfileModel> = new EventEmitter<ProfileModel>();

	static readonly wearableSummaryUpdated: EventEmitter<SummaryModel> = new EventEmitter<SummaryModel>();
	static readonly wearableWorkoutHistoryUpdated: EventEmitter<WorkoutModel[]> = new EventEmitter<WorkoutModel[]>();

	static readonly appDeeplinkEvent: EventEmitter<string> = new EventEmitter<string>();
	static readonly appPaused: EventEmitter<void> = new EventEmitter<void>();
	static readonly appResumed: EventEmitter<void> = new EventEmitter<void>();
	static readonly pageFocused: EventEmitter<AppAccessType> = new EventEmitter<AppAccessType>();
	static readonly notificationCount: EventEmitter<number> = new EventEmitter<number>();
	static readonly notificationHistoryUpdated: EventEmitter<Array<NotificationItemWithArticle>> = new EventEmitter<Array<NotificationItemWithArticle>>();
	static readonly gameAvailable: EventEmitter<boolean> = new EventEmitter<boolean>();
	static readonly environmentLoaded: EventEmitter<ILocalAppEnvironment> = new EventEmitter<ILocalAppEnvironment>();
	static readonly userUpdated: EventEmitter<UserData | null> = new EventEmitter<UserData | null>();
	static readonly profileDimensionsUpdated: EventEmitter<MemberProfileDimensionModel[]> = new EventEmitter<MemberProfileDimensionModel[]>();
	/** Emitted when server dimension questions/setup changes vs the prior cached setup. */
	static readonly profileDimensionsSetupChanged: EventEmitter<void> = new EventEmitter<void>();
	static readonly loadingEvent: EventEmitter<number> = new EventEmitter<number>();
	static readonly healthScoreHistoryUpdated: EventEmitter<HealthScoreHistory> = new EventEmitter<HealthScoreHistory>();
	static readonly faceScanHistoryUpdated: EventEmitter<ProfileScanHistoryModel> = new EventEmitter<ProfileScanHistoryModel>();
	static readonly organizationConfigLoaded: EventEmitter<OrganizationTokenModel> = new EventEmitter<OrganizationTokenModel>();

	static readonly onAIChatStart: EventEmitter<string> = new EventEmitter<string>();
	/** Emit to open the mini chat bubble with the given request (e.g. from insight icon tap). */
	static readonly openMiniChatRequest: EventEmitter<IInitialQuestion> = new EventEmitter<IInitialQuestion>();
	static readonly onBiometricListUpdated: EventEmitter<CurrentObservationStatusModel> = new EventEmitter<CurrentObservationStatusModel>();
	static readonly emotionHistoryUpdated: EventEmitter<EmotionsHistoryResponse[]> = new EventEmitter<EmotionsHistoryResponse[]>();
	static readonly networkStatusChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	static readonly aiBotEmotionChange: EventEmitter<VivaBotFaceState>= new EventEmitter<VivaBotFaceState>();
	static readonly aiBotAwakeState:EventEmitter<boolean>= new EventEmitter<boolean>();
	/** When true, app shell should hide the VivaBot (e.g. health score customise mode). */
	static readonly vivaBotHiddenByCustomiseMode: EventEmitter<boolean> = new EventEmitter<boolean>();
	// Use Subject instead of EventEmitter for reliable RxJS subscription behavior
	static readonly aiBotMessage: Subject<string> = new Subject<string>();
	/** Static intro bubble text (no More Info button), e.g. questionnaire page welcome. */
	static readonly aiBotIntroMessage: Subject<string> = new Subject<string>();
	/** Request the app shell to dismiss the VivaBot mini chat bubble. */
	static readonly closeMiniChatRequest: Subject<void> = new Subject<void>();
	static readonly revcatEntitlementsActive: EventEmitter<void> = new EventEmitter<void>();

	// Silent notification events for datapoints refresh
	static readonly biometricRefreshRequired: EventEmitter<void> = new EventEmitter<void>();

	/** Triggers coordinated profile/health-score data refresh across the app. */
	static readonly refreshProfileData: EventEmitter<RefreshProfileDataPayload> = new EventEmitter<RefreshProfileDataPayload>();
	
	// Questionnaire events
	static readonly questionnaireListUpdated: EventEmitter<any[]> = new EventEmitter<any[]>();
	static readonly questionnaireHistoryUpdated: EventEmitter<MemberQuestionnaireHistoryModel> = new EventEmitter<MemberQuestionnaireHistoryModel>();
	static readonly questionnaireSubmitted: EventEmitter<any> = new EventEmitter<any>();
	static readonly requestedQuestionnairesUpdated: EventEmitter<QuestionnaireRequestListItemModel[]> = new EventEmitter<QuestionnaireRequestListItemModel[]>();
}
